import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import flatpickr from 'flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import 'flatpickr/dist/themes/material_blue.css';
import '../PayjoyAudit.css';
import { useAuth } from '../provider/authProvider';
import PreloadFetch from '../components/PreloadFetch';
import ScrollToTop from '../components/ScrollTop';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_BASE = process.env.REACT_APP_API || process.env.REACT_APP_API_URL || '';
const ALLOWED_ROLES = [1, 5, 8];

const todayLima = () => {
    const now = new Date();
    const lima = new Date(now.toLocaleString('en-US', { timeZone: 'America/Lima' }));
    return lima.toISOString().slice(0, 10);
};

const formatFlatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatMoney = (value) => {
    const n = Number(value || 0);
    return `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};



const safeText = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') return fallback;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (Array.isArray(value)) {
        const text = value.map((item) => safeText(item, '')).filter(Boolean).join(', ');
        return text || fallback;
    }

    if (typeof value === 'object') {
        return (
            value.name ||
            value.makeModel ||
            value.description ||
            value.model ||
            value.label ||
            value.title ||
            fallback
        );
    }

    return fallback;
};

const getDeviceLabel = (registerPhone, payjoyDeviceName) => {
    const localPhone = safeText(registerPhone, '');
    if (localPhone) return localPhone;
    return safeText(payjoyDeviceName, '-');
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('es-PE', {
        timeZone: 'America/Lima',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getAuthHeaders = ({ user, rol }) => {
    const token = localStorage.getItem('token') || '';

    return {
        user: user || '',
        rol: rol || '',
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

const statusOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: 'MATCH_OK', label: 'Correctos' },
    { value: 'DOWN_PAYMENT_DIFF', label: 'Inicial no coincide' },
    { value: 'PURCHASE_AMOUNT_DIFF', label: 'Precio no coincide' },
    { value: 'AMOUNT_AND_PRICE_DIFF', label: 'Inicial y precio no coinciden' },
    { value: 'NO_MATCH', label: 'Sin match de IMEI' },
    { value: 'LOCAL_WITHOUT_PAYJOY', label: 'Registro sin PayJoy' },
];

export default function PayjoyAudit(props) {
    const { user, rol } = useAuth();
    const currentRole = Number(rol || 0);
    const hasAccess = ALLOWED_ROLES.includes(currentRole);
    const currentDay = useMemo(() => todayLima(), []);

    const [range, setRange] = useState([currentDay, currentDay]);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [auditMeta, setAuditMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('ALL');

    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);

    const startDate = range?.[0] || currentDay;
    const endDate = range?.[1] || range?.[0] || currentDay;

    const onDateRangeChange = useCallback(
        (selectedDates) => {
            if (!selectedDates || selectedDates.length === 0) {
                setRange([currentDay, currentDay]);
                return;
            }

            const values = selectedDates.map(formatFlatDate).filter(Boolean);

            if (values.length === 1) {
                setRange([values[0], values[0]]);
                return;
            }

            if (values.length >= 2) {
                setRange([values[0], values[1]]);
            }
        },
        [currentDay]
    );

    const fetchAudit = useCallback(async () => {
        if (!hasAccess) return;

        setLoading(true);
        setError('');

        try {
            const response = await axios.get(`${API_BASE}/payjoy-audit`, {
                params: { startDate, endDate },
                headers: getAuthHeaders({ user, rol }),
            });
            const payload = response.data || {};
            setRows(payload.rows || []);
            setSummary(payload.summary || null);
            setAuditMeta({
                fallbackApplied: Boolean(payload.fallbackApplied),
                fallbackReason: payload.fallbackReason || '',
                fallbackCutoff: payload.fallbackCutoff || '',
                requestedStartDate: payload.requestedStartDate || payload.startDate || startDate,
                requestedEndDate: payload.requestedEndDate || payload.endDate || endDate,
                effectiveStartDate: payload.effectiveStartDate || payload.startDate || startDate,
                effectiveEndDate: payload.effectiveEndDate || payload.endDate || endDate,
            });
        } catch (err) {
            setRows([]);
            setSummary(null);
            setAuditMeta(null);
            setError(err?.response?.data?.message || 'No se pudo cargar la auditoría de PayJoy.');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, user, rol, hasAccess]);

    useEffect(() => {
        if (!hasAccess) return undefined;

        flatpickr.localize(Spanish);

        if (calendarInputRef.current && !calendarInstanceRef.current) {
            calendarInstanceRef.current = flatpickr(calendarInputRef.current, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                locale: Spanish,
                allowInput: true,
                closeOnSelect: false,
                maxDate: currentDay,
                defaultDate: [currentDay, currentDay],
                onChange: onDateRangeChange,
            });
        }

        return () => {
            try {
                calendarInstanceRef.current?.destroy?.();
            } catch (flatpickrError) {
                console.warn('flatpickr payjoy audit destroy error:', flatpickrError);
            }
            calendarInstanceRef.current = null;
        };
    }, [currentDay, hasAccess, onDateRangeChange]);

    useEffect(() => {
        if (hasAccess) fetchAudit();
    }, [fetchAudit, hasAccess]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();

        return rows.filter((row) => {
            const statusOk = status === 'ALL' || row.status === status;
            if (!statusOk) return false;
            if (!q) return true;

            const text = [
                row.statusLabel,
                row.payjoyAccountLabel,
                row.payjoy?.accountLabel,
                row.payjoy?.imei,
                row.payjoy?.imeiLast6,
                row.payjoy?.customerName,
                safeText(row.payjoy?.deviceName, ''),
                row.register?.imei,
                row.register?.imeiLast6,
                row.register?.customerName,
                row.register?.dni,
                safeText(row.register?.phone, ''),
                row.register?.advisor_name,
                row.register?.sede_name,
                row.register?.city_name,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return text.includes(q);
        });
    }, [rows, search, status]);

    const displayRows = useMemo(() => {
        return [...filteredRows].reverse();
    }, [filteredRows]);

    const counters = summary || {
        total: 0,
        ok: 0,
        withDifferences: 0,
        noMatch: 0,
        localWithoutPayjoy: 0,
    };

    if (!hasAccess) {
        return (
            <div>
                <ScrollToTop />
                <Header user={user} namePage={props.namePage || 'payjoyAuditPage'} />
                <section className="sectionBody sectionBodyPayjoyAudit">
                    <div className="containerCustom containerBody">
                        <div className="blockBody">
                            <div className="payjoyAuditPage">
                                <div className="payjoyAuditForbidden">
                                    <span className="payjoyAuditEyebrow">Acceso restringido</span>
                                    <h1>Auditoría PayJoy</h1>
                                    <p>Esta página está disponible para Administrador, Cobranza y Contabilidad.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div>
            {loading && <PreloadFetch />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage || 'payjoyAuditPage'} />
            <section className="sectionBody sectionBodyPayjoyAudit">
                <div className="containerCustom containerBody">
                    <div className="blockBody">
                        <div className="payjoyAuditPage">
                            <div className="payjoyAuditHeader">
                                <div>
                                    <span className="payjoyAuditEyebrow">Auditoría diaria</span>
                                    <h1>Validación de ventas PayJoy</h1>
                                    <p>
                                        Compara las ventas finance de PayJoy contra tus registros locales usando los últimos 6 dígitos del IMEI.
                                    </p>
                                </div>

                                <button className="payjoyAuditRefresh" type="button" onClick={fetchAudit} disabled={loading}>
                                    {loading ? 'Actualizando...' : 'Actualizar'}
                                </button>
                            </div>

                            <div className="payjoyAuditFilters">
                                <div className="payjoyAuditFilterItem">
                                    <label>Rango de fechas</label>
                                    <input
                                        ref={calendarInputRef}
                                        type="text"
                                        className="payjoyAuditInput"
                                        placeholder="Seleccionar rango de fechas"
                                        readOnly
                                    />
                                </div>

                                <div className="payjoyAuditFilterItem">
                                    <label>Estado</label>
                                    <select className="payjoyAuditInput" value={status} onChange={(e) => setStatus(e.target.value)}>
                                        {statusOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="payjoyAuditFilterItem payjoyAuditFilterGrow">
                                    <label>Buscar</label>
                                    <input
                                        className="payjoyAuditInput"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Buscar por IMEI, cliente, DNI, asesor, sede..."
                                    />
                                </div>
                            </div>

                            {error && <div className="payjoyAuditError">{error}</div>}

                            {auditMeta?.fallbackApplied && !error && (
                                <div className="payjoyAuditError" style={{ borderColor: '#f59e0b', background: '#fffbeb', color: '#92400e' }}>
                                    <strong>PayJoy cerró el día actual.</strong> {auditMeta.fallbackReason}
                                    <br />
                                    Rango solicitado: {auditMeta.requestedStartDate} al {auditMeta.requestedEndDate}. Mostrando: {auditMeta.effectiveStartDate} al{' '}
                                    {auditMeta.effectiveEndDate}.
                                </div>
                            )}

                            <div className="payjoyAuditSummaryGrid">
                                <div className="payjoyAuditCard neutral">
                                    <span>Total revisado</span>
                                    <strong>{counters.total}</strong>
                                </div>
                                <div className="payjoyAuditCard success">
                                    <span>Coinciden</span>
                                    <strong>{counters.ok}</strong>
                                </div>
                                <div className="payjoyAuditCard warning">
                                    <span>Con diferencias</span>
                                    <strong>{counters.withDifferences}</strong>
                                </div>
                                <div className="payjoyAuditCard danger">
                                    <span>Sin match IMEI</span>
                                    <strong>{counters.noMatch}</strong>
                                </div>
                                <div className="payjoyAuditCard muted">
                                    <span>Local sin PayJoy</span>
                                    <strong>{counters.localWithoutPayjoy}</strong>
                                </div>
                            </div>

                            <div className="payjoyAuditTableWrap">
                                <table className="payjoyAuditTable">
                                    <thead>
                                        <tr>
                                            <th>Estado</th>
                                            <th>IMEI PayJoy</th>
                                            <th>IMEI sistema</th>
                                            <th>Cliente / Asesor</th>
                                            <th>Equipo</th>
                                            <th>Inicial PayJoy</th>
                                            <th>Inicial sistema</th>
                                            <th>Precio PayJoy</th>
                                            <th>Precio sistema</th>
                                            <th>Diferencia</th>
                                            <th>Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="11" className="payjoyAuditEmpty">
                                                    Cargando auditoría...
                                                </td>
                                            </tr>
                                        ) : displayRows.length === 0 ? (
                                            <tr>
                                                <td colSpan="11" className="payjoyAuditEmpty">
                                                    No hay registros para mostrar.
                                                </td>
                                            </tr>
                                        ) : (
                                            displayRows.map((row, index) => (
                                                <tr key={`${row.status}-${row.payjoy?.payjoyId || row.register?.id || index}`} className={`row-${row.severity}`}>
                                                    <td>
                                                        <span className={`payjoyAuditBadge ${row.severity}`}>{safeText(row.statusLabel)}</span>
                                                        {row.duplicateLocalMatches && <small className="payjoyAuditNote">IMEI duplicado en sistema</small>}
                                                    </td>
                                                    <td>
                                                        <strong>{row.payjoy?.imeiLast6 || '-'}</strong>
                                                        <small>{row.payjoy?.imei || '-'}</small>
                                                        <small>
                                                            Cuenta: {safeText(row.payjoyAccountLabel || row.payjoy?.accountLabel)}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <strong>{row.register?.imeiLast6 || '-'}</strong>
                                                        <small>{row.register?.imei || '-'}</small>
                                                    </td>
                                                    <td>
                                                        <strong>{safeText(row.register?.customerName || row.payjoy?.customerName)}</strong>
                                                        <small>
                                                            {safeText(row.register?.advisor_name)} · {safeText(row.register?.sede_name)}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <strong>{getDeviceLabel(row.register?.phone, row.payjoy?.deviceName)}</strong>
                                                        <small>{row.register?.dni ? `DNI ${safeText(row.register.dni)}` : safeText(row.message)}</small>
                                                    </td>
                                                    <td>{row.payjoy ? formatMoney(row.payjoy.downPayment) : '-'}</td>
                                                    <td>
                                                        {row.register ? formatMoney(row.register.systemDownPayment) : '-'}
                                                        {row.register && (
                                                            <small>
                                                                Efec. {formatMoney(row.register.cash_amount)} · Online {formatMoney(row.register.online_amount)} · FM{' '}
                                                                {formatMoney(row.register.fm_amount)}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>{row.payjoy ? formatMoney(row.payjoy.purchaseAmount) : '-'}</td>
                                                    <td>{row.register ? formatMoney(row.register.phone_price) : '-'}</td>
                                                    <td>
                                                        <strong>{row.downPaymentDiff !== undefined ? formatMoney(row.downPaymentDiff) : '-'}</strong>
                                                        <small>Precio: {row.purchaseDiff !== undefined ? formatMoney(row.purchaseDiff) : '-'}</small>
                                                    </td>
                                                    <td>{formatDateTime(row.register?.creation_date || row.payjoy?.createdAt)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
